import React, { useMemo } from "react";
import "./Payment.scss";
import { useTranslation } from "react-i18next";
import * as intrfce from '../Interfaces'
import { useLocalization } from "../../../contexts/LocalizationContext";
import { LOCALES_WITHOUT_NVSTORE_PAYMENT_CONFIG } from "../../../constants/localesWithoutNvstorePaymentConfig";
declare global {
    namespace JSX {
        interface IntrinsicElements {
            'guest-payment-method-v2': {
                name: string;
                email?: string;
                key: string;
                address_line_1?: string;
                address_line_2?: string;
                city?: string;
                state?: string;
                country?: string;
                postal_code?: string;
                save_payment: boolean;
                full_address?: boolean;
                has_name?: boolean;
                'payment-config'?: string;
            };
            'registered-user-payment-methods-v2': {
                name: string;
                email?: string;
                key: string;
                address_line_1?: string;
                address_line_2?: string;
                city?: string;
                state?: string;
                country?: string;
                postal_code?: string;
                full_address?: boolean;
                has_name?: boolean;
                'payment-config'?: string;
            };
        }
    }
}

interface Props {
    billingData: any;
    isLoggedIn: boolean;
    savePayment: boolean;
    validationErrors: any;
    formBillingAddress: intrfce.iAddressFormData;
    formBillingData: intrfce.iBillingFormData;
}

const Payment: React.FC<Props> = ({ billingData, isLoggedIn, savePayment, validationErrors, formBillingAddress, formBillingData }) => {
    const { t } = useTranslation();
    const { stateDropdown, locale, postalCodeRequired } = useLocalization();
    const paymentConfigAttr = LOCALES_WITHOUT_NVSTORE_PAYMENT_CONFIG.includes(locale)
        ? {}
        : { 'payment-config': 'nvstore-standard-payments' as const };

    const hasAllRequiredPropsForGuestUser = useMemo(() => {
        const firstName = formBillingData?.firstNameBilling;
        const lastName = formBillingData?.lastNameBilling;
        const email = formBillingData?.emailBilling;
        const country = formBillingAddress?.countryValue;
        const postal_code = formBillingAddress?.pincode;
        const address = formBillingAddress?.address;
        const city = formBillingAddress?.cityValue;
        const state = locale === 'zh-tw' ? "TW" : formBillingAddress?.stateValue;

        return firstName?.trim() && lastName?.trim() && email?.trim() && country?.trim() && (postalCodeRequired ? postal_code?.trim() : true) && address?.trim() && city?.trim() && (stateDropdown || locale === 'zh-tw' || (locale === 'es-la' && formBillingAddress['countryValue'] === 'PA') ? state?.trim() : true);
    }, [formBillingData, formBillingAddress, locale, postalCodeRequired, stateDropdown]);

    const hasAllRequiredPropsForRegisteredUser = useMemo(() => {
        // Prioritize form input over saved billing data, use ?? to properly handle empty strings
        const firstName = formBillingData?.firstNameBilling ?? billingData?.firstName;
        const lastName = formBillingData?.lastNameBilling ?? billingData?.lastName;
        const email = formBillingData?.emailBilling ?? billingData?.email;
        const country = formBillingAddress?.countryValue ?? billingData?.country;
        const postal_code = formBillingAddress?.pincode ?? billingData?.postalCode;
        const address = formBillingAddress?.address ?? billingData?.address;
        const city = formBillingAddress?.cityValue ?? billingData?.city;
        const state = locale === 'zh-tw' ? "TW" : (formBillingAddress?.stateValue ?? billingData?.state);

        return firstName?.trim() && lastName?.trim() && email?.trim() && country?.trim() && (postalCodeRequired ? postal_code?.trim() : true) && address?.trim() && city?.trim() && (stateDropdown || locale === 'zh-tw' || (locale === 'es-la' && formBillingAddress['countryValue'] === 'PA') ? state?.trim() : true);
    }, [billingData, formBillingData, formBillingAddress, locale, postalCodeRequired, stateDropdown]);
    // Memoize keys to prevent unnecessary re-renders - only changes when actual data changes
    const billingDataKey = useMemo(() => {
        return formBillingAddress ? JSON.stringify(formBillingAddress) : JSON.stringify(billingData);
    }, [billingData, formBillingAddress]);

    const guestDataKey = useMemo(() => {
        return JSON.stringify({
            address: formBillingAddress,
            data: formBillingData
        });
    }, [formBillingAddress, formBillingData]);

    return (
        <div className="payment mb-4">
            <h3 className={`PaymentHeader`}>{t('PAYMENT_INFORMATION')}</h3>

            {validationErrors.paymentSrc && <p className="text-danger" style={{ color: '#dc3545', margin: '10px 0' }}>{validationErrors.paymentSrc}</p>}
            {
                isLoggedIn ? (
                    hasAllRequiredPropsForRegisteredUser ? (
                        <>
                            <registered-user-payment-methods-v2
                                name={`${billingData?.firstName || formBillingData?.firstNameBilling} ${billingData?.lastName || formBillingData?.lastNameBilling}`}
                                key={billingDataKey}
                                email={billingData?.email || formBillingData?.emailBilling}
                                country={formBillingAddress?.countryValue || billingData?.country || ''}
                                postal_code={postalCodeRequired ? (formBillingAddress?.pincode ?? billingData?.postalCode ?? '') : ''}
                                address_line_1={formBillingAddress?.address ?? billingData?.address ?? ''}
                                address_line_2={formBillingAddress?.line2 ?? billingData?.line2 ?? ''}
                                city={formBillingAddress?.cityValue ?? billingData?.city ?? ''}
                                state={locale === 'zh-tw' ? "TW" : (formBillingAddress?.stateValue ?? billingData?.state ?? '')}
                                full_address={true}
                                has_name={true}
                                {...paymentConfigAttr}
                            />
                        </>
                    ) : (
                            <RequiredFieldsAlert message={t('COMPLETE_THE_REQUIRED_FIELDS')} />
                    )
                ) : (
                        hasAllRequiredPropsForGuestUser ? (
                            <guest-payment-method-v2
                                key={guestDataKey}
                            name={`${formBillingData?.firstNameBilling} ${formBillingData?.lastNameBilling}`}
                            email={formBillingData?.emailBilling}
                            country={formBillingAddress?.countryValue || ''}
                            postal_code={postalCodeRequired ? (formBillingAddress?.pincode || '') : ''}
                            save_payment={savePayment}
                            address_line_1={formBillingAddress?.address}
                            address_line_2={formBillingAddress?.line2}
                            city={formBillingAddress?.cityValue}
                            state={formBillingAddress?.stateValue}
                                full_address={true}
                                has_name={true}
                                {...paymentConfigAttr}
                        />
                    ) : (
                                <RequiredFieldsAlert message={t('COMPLETE_THE_BILLING_INFORMATION')} />
                    )
                )
            }

        </div >
    )
}

// Convert to proper React component to avoid creating new elements on every render
const RequiredFieldsAlert: React.FC<{ message: string }> = React.memo(({ message }) => {
    return (
        <div
            className="payment-required-fields-alert"
            role="alert"
            aria-live="polite"
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M12 2L2 20h20L12 2zm0 3.5L19.53 19H4.47L12 5.5zM11 10v4h2v-4h-2zm0 5v2h2v-2h-2z"
                    fill="#B86E00"
                />
            </svg>
            <span className="payment-required-fields-message">
                {message}
            </span>
        </div>
    );
});

RequiredFieldsAlert.displayName = 'RequiredFieldsAlert';

export default Payment;